/*
 * Headless WFS/WFS-T client backed by OpenLayers encoders.
 *
 * The client owns WFS XML generation, same-origin HTTP requests, response
 * parsing, transaction summaries, and OWS exception normalization without
 * depending on Leaflet or another rendering framework.
 */
import WFS from "ol/format/WFS.js";
import GML3 from "ol/format/GML3.js";
import GeoJSON from "ol/format/GeoJSON.js";
import {createWfsFeature, olFeatureToWfsFeature, wfsFeaturesToOlFeatures} from "@/engine/wfst/wfsFeature.js";

const DEFAULT_WFS_VERSION = "1.1.0";
const DEFAULT_GEOMETRY_NAME = "geom";
const DEFAULT_SRS_NAME = "EPSG:4326";
// GeoServer WFS-T layers in this app expect lon/lat GML coordinates; CRS:84 keeps that axis order for WFS 1.1 transactions.
const DEFAULT_TRANSACTION_SRS_NAME = "CRS:84";
const JSON_OUTPUT_FORMAT = "application/json";

/**
 * Error thrown when a WFS service returns an OWS ExceptionReport or bad response.
 */
export class WfsServiceError extends Error {
    /**
     * Builds a normalized WFS service error.
     * @param {string} message Public diagnostic message.
     * @param {Array<{code?: string, locator?: string, text: string}>} [exceptions] Parsed OWS exceptions.
     */
    constructor(message, exceptions = []) {
        super(message);
        this.name = "WfsServiceError";
        this.exceptions = exceptions;
    }
}

export class WfsTransactionalClient {
    /**
     * Creates a WFS/WFS-T client for one feature type.
     * @param {Object} options WFS endpoint and feature type options.
     * @param {string} options.url WFS endpoint URL.
     * @param {string} options.typeNS Feature namespace prefix.
     * @param {string} options.typeName Feature type name without prefix.
     * @param {string} [options.geometryName="geom"] Geometry attribute name.
     * @param {string} [options.version="1.1.0"] WFS protocol version.
     * @param {string} [options.srsName="EPSG:4326"] Feature coordinate reference system.
     * @param {string} [options.transactionSrsName="CRS:84"] CRS used for WFS-T geometry serialization.
     * @param {boolean} [options.forceMulti=false] Whether transactions force multi line/polygon geometry.
     * @param {Object} [options.headers] Extra request headers.
     * @param {boolean} [options.withCredentials=false] Whether fetch should include credentials explicitly.
     */
    constructor(options) {
        this.url = options.url;
        this.typeNS = options.typeNS;
        this.typeName = options.typeName;
        this.typeNSName = `${this.typeNS}:${this.typeName}`;
        this.geometryName = options.geometryName || DEFAULT_GEOMETRY_NAME;
        this.version = options.version || DEFAULT_WFS_VERSION;
        this.srsName = options.srsName || DEFAULT_SRS_NAME;
        this.transactionSrsName = options.transactionSrsName || DEFAULT_TRANSACTION_SRS_NAME;
        this.forceMulti = options.forceMulti || false;
        this.headers = options.headers || {};
        this.withCredentials = options.withCredentials || false;
        this.namespaceUri = options.namespaceUri || "";
        this.featureTypeDescription = null;
        this.geoJsonFormat = new GeoJSON({
            geometryName: this.geometryName,
        });
        this.wfsFormat = this._createWfsFormat();
        this.ready = this.describeFeatureType();
    }

    /**
     * Loads schema metadata required for WFS transactions.
     * @returns {Promise<Document>} Parsed DescribeFeatureType response.
     * @throws {WfsServiceError} When GeoServer returns an OWS exception or invalid XML.
     */
    async describeFeatureType() {
        const doc = document.implementation.createDocument("http://www.opengis.net/wfs", "wfs:DescribeFeatureType");
        const root = doc.documentElement;
        root.setAttribute("service", "WFS");
        root.setAttribute("version", this.version);
        const typeNameNode = doc.createElement("TypeName");
        typeNameNode.textContent = this.typeNSName;
        root.appendChild(typeNameNode);

        const xml = await this._postXml(root);
        const responseDoc = parseXmlDocument(xml);
        throwIfExceptionReport(responseDoc);

        this.featureTypeDescription = responseDoc;
        this.namespaceUri = responseDoc.documentElement.getAttribute("targetNamespace") || this.namespaceUri;
        this.wfsFormat = this._createWfsFormat();
        return responseDoc;
    }

    /**
     * Loads features using WFS GetFeature.
     * @param {import("ol/format/filter/Filter.js").default|null} [filter] Optional OpenLayers WFS filter.
     * @returns {Promise<Array<Object>>} Neutral WFS features.
     * @throws {WfsServiceError} When the WFS request fails.
     */
    async loadFeatures(filter = null) {
        await this.ready;
        const node = this.wfsFormat.writeGetFeature({
            featureNS: this.namespaceUri,
            featurePrefix: this.typeNS,
            featureTypes: [this.typeName],
            srsName: this.srsName,
            outputFormat: JSON_OUTPUT_FORMAT,
            geometryName: this.geometryName,
            filter,
        });
        const responseText = await this._postXml(node);
        throwIfExceptionReport(parseXmlDocumentIfXml(responseText));
        return this._readFeatures(responseText);
    }

    /**
     * Loads features by server feature ids.
     * @param {Array<string>} ids Server feature ids returned from a transaction.
     * @returns {Promise<Array<Object>>} Neutral WFS features.
     * @throws {WfsServiceError} When the WFS request fails.
     */
    async loadFeaturesByIds(ids) {
        const features = [];
        for (const id of ids) {
            const responseText = await this._postXml(this._getFeatureByIdNode(id));
            throwIfExceptionReport(parseXmlDocumentIfXml(responseText));
            features.push(...this._readFeatures(responseText));
        }
        return features;
    }

    /**
     * Saves inserted, updated, and deleted features in one WFS transaction.
     * @param {{inserts?: Array<Object>, updates?: Array<Object>, deletes?: Array<Object>}} changes Neutral feature changes.
     * @returns {Promise<{responseText: string, transactionResponse: Object}>} Transaction response details.
     * @throws {WfsServiceError} When GeoServer rejects the transaction.
     */
    async saveChanges(changes) {
        await this.ready;
        const inserts = wfsFeaturesToOlFeatures(changes.inserts || [], this._olWriteOptions());
        const updates = wfsFeaturesToOlFeatures(changes.updates || [], this._olWriteOptions());
        const deletes = wfsFeaturesToOlFeatures(changes.deletes || [], this._olWriteOptions());
        const node = this.wfsFormat.writeTransaction(inserts, updates, deletes, this._transactionOptions());
        const responseText = await this._postXml(node);
        const responseDoc = parseXmlDocument(responseText);
        throwIfExceptionReport(responseDoc);
        const transactionResponse = this.wfsFormat.readTransactionResponse(responseDoc) || {};
        return {
            responseText,
            transactionResponse,
        };
    }

    /**
     * Creates an OpenLayers WFS format configured for this feature type.
     * @returns {WFS} Configured WFS formatter.
     */
    _createWfsFormat() {
        return new WFS({
            version: this.version,
            featureNS: this.namespaceUri || undefined,
            featureType: this.typeName,
            gmlFormat: new GML3(this._gmlOptions(this.transactionSrsName)),
        });
    }

    /**
     * Returns GML options matching the current GeoServer feature type.
     * @param {string} srsName SRS name used by serialized geometries.
     * @returns {Object} OpenLayers GML3 options.
     */
    _gmlOptions(srsName) {
        return {
            featureNS: this.namespaceUri || undefined,
            featureType: this.typeName,
            srsName,
            multiCurve: true,
            multiSurface: false,
        };
    }

    /**
     * Returns transaction options for OpenLayers WFS serialization.
     * @returns {Object} OpenLayers WFS transaction options.
     */
    _transactionOptions() {
        return {
            version: this.version,
            featureNS: this.namespaceUri,
            featurePrefix: this.typeNS,
            featureType: this.typeName,
            srsName: this.transactionSrsName,
            gmlOptions: this._gmlOptions(this.transactionSrsName),
        };
    }

    /**
     * Returns conversion options for neutral feature to OpenLayers feature.
     * @returns {{geometryName: string, forceMulti: boolean}} Conversion options.
     */
    _olWriteOptions() {
        return {
            geometryName: this.geometryName,
            forceMulti: this.forceMulti,
        };
    }

    /**
     * Creates a WFS 1.1 GetFeature request filtered by a GeoServer feature id.
     *
     * GeoServer returns WFS 1.1 InsertResults as ogc:FeatureId values. OpenLayers
     * resourceId() follows the WFS 2.0 ResourceId path and fails in this WFS 1.1
     * GetFeature serializer combination, so this compatibility request mirrors
     * the legacy leaflet-wfst GmlObjectId filter.
     *
     * @param {string} id Feature id returned by WFS Transaction InsertResults.
     * @returns {Element} XML request node.
     */
    _getFeatureByIdNode(id) {
        const doc = document.implementation.createDocument("http://www.opengis.net/wfs", "wfs:GetFeature");
        const root = doc.documentElement;
        root.setAttribute("service", "WFS");
        root.setAttribute("version", this.version);
        root.setAttribute("outputFormat", JSON_OUTPUT_FORMAT);
        root.setAttribute("xmlns:ogc", "http://www.opengis.net/ogc");
        root.setAttribute("xmlns:gml", "http://www.opengis.net/gml");

        const query = doc.createElementNS("http://www.opengis.net/wfs", "wfs:Query");
        query.setAttribute("typeName", this.typeNSName);
        query.setAttribute("srsName", this.srsName);
        root.appendChild(query);

        const filter = doc.createElementNS("http://www.opengis.net/ogc", "ogc:Filter");
        const objectId = doc.createElementNS("http://www.opengis.net/ogc", "ogc:GmlObjectId");
        objectId.setAttributeNS("http://www.opengis.net/gml", "gml:id", id);
        filter.appendChild(objectId);
        query.appendChild(filter);

        return root;
    }

    /**
     * Parses a WFS feature response into neutral features.
     * @param {string} responseText Raw WFS response.
     * @returns {Array<Object>} Neutral WFS features.
     */
    _readFeatures(responseText) {
        const trimmed = responseText.trim();
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
            const olFeatures = this.geoJsonFormat.readFeatures(responseText, {
                dataProjection: "EPSG:4326",
                featureProjection: "EPSG:4326",
            });
            return olFeatures.map((feature) => olFeatureToWfsFeature(feature, this.geometryName));
        }
        const olFeatures = this.wfsFormat.readFeatures(responseText, {
            dataProjection: this.srsName,
            featureProjection: "EPSG:4326",
        });
        return olFeatures.map((feature) => olFeatureToWfsFeature(feature, this.geometryName));
    }

    /**
     * Posts an XML node to the WFS endpoint.
     * @param {Node} node XML request node.
     * @returns {Promise<string>} Raw response text.
     * @throws {WfsServiceError} When HTTP status is not successful.
     */
    async _postXml(node) {
        const response = await fetch(this.url, {
            method: "POST",
            body: new XMLSerializer().serializeToString(node),
            headers: {
                "Content-Type": "text/xml",
                ...this.headers,
            },
            credentials: this.withCredentials ? "include" : "same-origin",
        });
        const text = await response.text();
        if (!response.ok) {
            throw new WfsServiceError(`WFS request failed with HTTP ${response.status}`, [{
                text,
            }]);
        }
        return text;
    }
}

/**
 * Parses XML text into a DOM document.
 * @param {string} text XML text.
 * @returns {Document} Parsed document.
 * @throws {WfsServiceError} When XML parsing fails.
 */
function parseXmlDocument(text) {
    const doc = new DOMParser().parseFromString(text, "text/xml");
    const parserError = doc.getElementsByTagName("parsererror")[0];
    if (parserError) {
        throw new WfsServiceError("WFS response is not valid XML", [{
            text: parserError.textContent || "XML parser error",
        }]);
    }
    return doc;
}

/**
 * Parses XML-like response text and ignores JSON responses.
 * @param {string} text Raw response text.
 * @returns {Document|null} Parsed XML document or null for JSON.
 */
function parseXmlDocumentIfXml(text) {
    const trimmed = text.trim();
    if (!trimmed.startsWith("<")) {
        return null;
    }
    return parseXmlDocument(text);
}

/**
 * Throws a normalized error when the document is an OWS exception report.
 * @param {Document|null} doc Parsed XML document.
 * @throws {WfsServiceError} When an exception report is detected.
 */
function throwIfExceptionReport(doc) {
    if (!doc) {
        return;
    }
    const root = doc.documentElement;
    if (!root || !root.localName || root.localName !== "ExceptionReport") {
        return;
    }
    const exceptions = Array.from(root.getElementsByTagNameNS("*", "Exception")).map((node) => ({
        code: node.getAttribute("exceptionCode") || undefined,
        locator: node.getAttribute("locator") || undefined,
        text: Array.from(node.getElementsByTagNameNS("*", "ExceptionText")).map((textNode) => textNode.textContent).join("\n"),
    }));
    throw new WfsServiceError(exceptions[0]?.text || "WFS service returned an exception", exceptions);
}

export {createWfsFeature};
